import React from 'react';
import ErrorWrapper from '@/app/ErrorWrapper';
import { useEffect, useState } from 'react';
import { notification, Modal, Button } from 'antd';
import { lessonsApi } from '@/api/lessons/lessonsApi';
import { Controller, useForm } from 'react-hook-form';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import moment from 'moment';
import { dateStringToYYYYMMDD } from '@/utils/functions';
import { useTranslation } from 'react-i18next'; // Added import

// --- ADDED --- Default values for resetting the form
const defaultFormValues = {
    courseId: '',
    link: '',
    description: '',
    startDate: moment().toDate(),
    endDate: moment().add(1, 'days').toDate()
};

const LessonModalComponent = ({ open, handleToggleModal, id, courseId }) => {
    const { t } = useTranslation(); // Added hook
    const [isLoadingDetail, setIsLoadingDetail] = useState(false);
    const { handleSubmit, control, reset } = useForm({
        defaultValues: defaultFormValues
    });

    const getDetail = () => {
        setIsLoadingDetail(true);
        lessonsApi
            .getDetail(id)
            .then((res) => {
                const detail = res.data.data;
                reset({
                    // Reset with fetched data
                    courseId: detail?.courseId,
                    link: detail?.link,
                    description: detail?.description,
                    startDate: detail?.startDate ? moment(detail?.startDate).toDate() : null,
                    endDate: detail?.endDate ? moment(detail?.endDate).toDate() : null
                });
            })
            .catch((err) => {
                // --- UPDATED ---: Add error notification for fetching details
                notification.error({
                    message: t('LessonModal_Admin_Fetch_Failed_Title'),
                    description: t('LessonModal_Admin_Fetch_Failed_Description')
                });
                console.error('Fetch detail error:', err);
                handleToggleModal(); // Close modal if detail fetch fails
            })
            // --- ADDED ---: Ensure loading stops
            .finally(() => {
                setIsLoadingDetail(false);
            });
    };

    // --- ADDED ---: Function to handle form validation errors
    const onFormError = (errors) => {
        console.error('Form validation errors:', errors);
        notification.warning({
            message: t('LessonModal_Admin_Validation_MissingFields_Title'),
            description: t('LessonModal_Admin_Validation_MissingFields_Description')
        });
    };

    const submitForm = (formPayload) => {
        setIsLoadingDetail(true);
        if (id) {
            // Update existing lesson
            lessonsApi
                .update({
                    ...formPayload,
                    startDate: formPayload.startDate,
                    endDate: formPayload.endDate,
                    id: id
                })
                .then((res) => {
                    notification.success({ message: t('LessonModal_Admin_Submit_UpdateSuccess') });
                    handleToggleModal(); // Close modal on success
                })
                // --- UPDATED ---: Add error notification
                .catch((err) => {
                    notification.error({
                        message: t('LessonModal_Admin_Submit_UpdateFailed_Title'),
                        description:
                            err.response?.data?.message ||
                            t('LessonModal_Admin_Submit_Failed_Description')
                    });
                    console.error('Update error:', err);
                })
                // --- ADDED ---: Ensure loading stops
                .finally(() => {
                    setIsLoadingDetail(false);
                });
        } else {
            // Create new lesson
            lessonsApi
                .create({
                    ...formPayload,
                    courseId: courseId, // Use courseId prop for new lessons
                    startDate: formPayload.startDate,
                    endDate: formPayload.endDate
                })
                .then((res) => {
                    notification.success({ message: t('LessonModal_Admin_Submit_CreateSuccess') });
                    reset(defaultFormValues); // Reset form to defaults on successful creation
                    handleToggleModal(); // Close modal on success
                })
                // --- UPDATED ---: Add error notification
                .catch((err) => {
                    notification.error({
                        message: t('LessonModal_Admin_Submit_CreateFailed_Title'),
                        description:
                            err.response?.data?.message ||
                            t('LessonModal_Admin_Submit_Failed_Description_Create')
                    });
                    console.error('Create error:', err);
                })
                // --- ADDED ---: Ensure loading stops
                .finally(() => {
                    setIsLoadingDetail(false);
                });
        }
    };

    useEffect(() => {
        if (id) {
            getDetail();
        } else {
            // --- ADDED --- Reset to default values when creating a new lesson
            reset(defaultFormValues);
        }
    }, [id, open]); // Re-run effect if modal opens or id changes

    return (
        <Modal
            open={open}
            title={id ? t('LessonModal_Admin_Title_Edit') : t('LessonModal_Admin_Title_Create')} // Dynamic title
            onCancel={handleToggleModal}
            confirmLoading={isLoadingDetail} // Use confirmLoading for button state
            destroyOnClose={true} // Reset form state when modal closes
            footer={[
                // Custom footer using Ant Design Buttons
                <Button
                    key="back"
                    onClick={handleToggleModal}
                    disabled={isLoadingDetail}>
                    {t('LessonModal_Admin_Button_Cancel')}
                </Button>,
                <Button
                    key="submit"
                    type="primary"
                    loading={isLoadingDetail}
                    onClick={handleSubmit(submitForm, onFormError)} // Trigger submit and validation
                >
                    {t('LessonModal_Admin_Button_Ok')}
                </Button>
            ]}>
            {/* Form is now inside Modal content, not separate */}
            <form
                id="lesson-form"
                onSubmit={handleSubmit(submitForm, onFormError)}>
                {' '}
                {/* Keep onSubmit for direct Enter key submission */}
                <div className="card-body">
                    {' '}
                    {/* Removed extra div and card-header */}
                    <div className="w-full">
                        <div className="form-group">
                            {/* --- UPDATED --- Added required asterisk */}
                            <label>
                                {t('LessonModal_Admin_Form_Description')}{' '}
                                <span style={{ color: 'red' }}>*</span>
                            </label>
                            <Controller
                                control={control}
                                rules={{ required: true }}
                                render={({ field }) => (
                                    <input
                                        {...field}
                                        type="text"
                                        className="form-control"
                                    />
                                )}
                                name="description"
                            />
                        </div>
                    </div>
                    <div className="flex gap-2 items-center ">
                        <div className="w-full">
                            <div className="form-group">
                                {/* --- UPDATED --- Added required asterisk */}
                                <label>
                                    {t('LessonModal_Admin_Form_StartDate')}{' '}
                                    <span style={{ color: 'red' }}>*</span>
                                </label>
                                <Controller
                                    control={control}
                                    name="startDate"
                                    // --- ADDED --- Required rule
                                    rules={{ required: true }}
                                    render={({ field }) => (
                                        <DatePicker
                                            {...field}
                                            showTimeSelect
                                            dateFormat="dd-MM-yyyy HH:mm:ss"
                                            className="form-control w-full" // Added w-full for consistency
                                            selected={field.value}
                                            placeholderText={t(
                                                'LessonModal_Admin_Form_StartDate_Placeholder'
                                            )} // Added placeholder
                                        />
                                    )}
                                />
                            </div>
                        </div>
                        <div className="w-full">
                            <div className="form-group">
                                {/* --- UPDATED --- Added required asterisk */}
                                <label>
                                    {t('LessonModal_Admin_Form_EndDate')}{' '}
                                    <span style={{ color: 'red' }}>*</span>
                                </label>
                                <Controller
                                    control={control}
                                    name="endDate"
                                    // --- ADDED --- Required rule
                                    rules={{ required: true }}
                                    render={({ field }) => (
                                        <DatePicker
                                            {...field}
                                            showTimeSelect
                                            dateFormat="dd-MM-yyyy HH:mm:ss"
                                            className="form-control w-full" // Added w-full
                                            selected={field.value}
                                            placeholderText={t(
                                                'LessonModal_Admin_Form_EndDate_Placeholder'
                                            )} // Added placeholder
                                        />
                                    )}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="w-full">
                        {/* --- UPDATED --- Added required asterisk */}
                        <label>
                            {t('LessonModal_Admin_Form_Link')}{' '}
                            <span style={{ color: 'red' }}>*</span>
                        </label>
                        <Controller
                            control={control}
                            rules={{ required: true }}
                            render={({ field }) => (
                                <input
                                    {...field}
                                    type="text"
                                    className="form-control"
                                    placeholder={t('LessonModal_Admin_Form_Link_Placeholder')} // Added placeholder
                                />
                            )}
                            name="link"
                        />
                    </div>
                </div>
            </form>
        </Modal>
    );
};

LessonModalComponent.displayName = 'LessonModal';

const LessonModal = (props) => {
    return (
        <ErrorWrapper>
            <LessonModalComponent
                data-id="LessonModal-cpn"
                {...props}
            />
        </ErrorWrapper>
    );
};

export default LessonModal;
